import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { Position } from '@recruitee/http-types';
import type {
  InterviewTemplate,
  InterviewTemplateKind,
} from '@recruitee/interview-templates-types';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  pipeState,
  selectRouteData,
} from '@recruitee/ngrx';

const { actions, initialState, namespace, reducer, selectors } =
  createEntityCollectionStore<InterviewTemplate>()('InterviewTemplates', {
    customActions: adapter => {
      const reducerFunctions = getEntityCollectionActions(
        adapter,
        ({ id }: { id?: number }) => id!,
      );

      return {
        references: (state, payload: { data: InterviewTemplate[] }) =>
          adapter.updateOrCreateMany(state, payload.data),

        fetchCollection: (state, payload: { scope: InterviewTemplateKind }) =>
          reducerFunctions.fetchCollection(state, payload),
        fetchCollectionSuccess: (
          state,
          payload: { data: InterviewTemplate[]; scope: InterviewTemplateKind },
        ) => reducerFunctions.fetchCollectionSuccess(state, payload),
        fetchCollectionFailed: (
          state,
          payload: { error: HttpErrorResponse; scope: InterviewTemplateKind },
        ) => reducerFunctions.fetchCollectionFailed(state, payload),

        updatePositions: (state, payload: { data: Position[] }) =>
          adapter.markAsPending(state, { entities: payload.data.map(({ id }) => id), scope: null }),
        updatePositionsSuccess: (state, payload: { data: InterviewTemplate[] }) =>
          pipeState(
            state,
            next =>
              adapter.markAsLoaded(next, {
                entities: payload.data.map(({ id }) => id!),
                scope: null,
              }),
            next => adapter.writeMany(next, payload.data),
          ),
        updatePositionsFailed: (state, payload: { error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error),
      };
    },
  });

const entitiesByKindFromRouter = createSelector(
  selectors.entityMap,
  selectRouteData,
  (entities, data) =>
    Object.values(entities)
      .filter(entity => entity!.kind === data.kind)
      .sort((a, b) => a!.position - b!.position) || [],
);

const mergedSelectors = {
  entitiesByKindFromRouter,
  ...selectors,
};

const InterviewTemplateActions = {
  references: actions.references,

  fetchCollection: actions.fetchCollection,
  fetchCollectionSuccess: actions.fetchCollectionSuccess,
  fetchCollectionFailed: actions.fetchCollectionFailed,

  updatePositions: actions.updatePositions,
  updatePositionsSuccess: actions.updatePositionsSuccess,
  updatePositionsFailed: actions.updatePositionsFailed,
};

export {
  actions,
  InterviewTemplateActions,
  namespace as InterviewTemplateNamespace,
  initialState as InterviewTemplateInitialState,
  reducer as InterviewTemplateReducer,
  mergedSelectors as InterviewTemplateSelectors,
};
